import { RFIMS_SITES } from '@/features/rfims/registry';
import { useEffect, useRef, useState, type FocusEvent, type PointerEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import {
  Bot,
  Building2,
  Home,
  Pin,
  PinOff,
  RadioTower,
  Satellite,
  UsersRound,
  Waypoints,
  type LucideIcon,
} from 'lucide-react';
import { getJson } from '@/lib/api';
import { useUI, type Mode } from '@/state/store';
import { cn } from '@/lib/cn';
import { LoaderStrip } from '@/features/loader/ui/LoaderStrip';
import { NOAA_CATALOGUE, noaaAssetId } from '@/features/loader/catalog/noaaCatalogue';
import { useMaintenanceCatalogue, type CatalogueLeaf } from '@/features/catalogue/useMaintenanceCatalogue';
import { useCatalogueView } from '@/features/catalogue/view';
import { registerAntennaTwin, registerMissionTwin } from '@/features/twin/data';
import { toast } from '@/components/ui/toast';
import { useLoader } from '@/features/loader/store';

const GLOBE_MODES = new Set<Mode>(['loader', 'operations', 'analysis']);

interface TreeNode {
  name: string;
  id: string;
  icon?: string;
  description?: string;
  isLeaf?: boolean;
  children?: TreeNode[];
}

interface Group {
  key: string;
  label: string;
  icon: LucideIcon;
  /** ids of groups in actionable_items.json that feed this strip entry */
  sources: string[];
}

const GROUPS: Group[] = [
  { key: 'overview', label: 'Overview', icon: Home, sources: [] },
  { key: 'satellites', label: 'Satellites', icon: Satellite, sources: ['satellite'] },
  { key: 'noaa', label: 'NOAA satellites', icon: Satellite, sources: [] },
  { key: 'antennas', label: 'Antennas', icon: RadioTower, sources: ['antennas'] },
  {
    key: 'stations',
    label: 'Ground stations',
    icon: Building2,
    sources: ['ground_stations', 'data_centers'],
  },
  {
    key: 'sensor-sources',
    label: 'Sensor sources',
    icon: Waypoints,
    sources: ['remote_sensing_platforms'],
  },
  { key: 'consumers', label: 'Consumers', icon: UsersRound, sources: ['end_users'] },
];

/**
 * Left rail: asset categories that expand to the assets themselves. The rail
 * keeps 64 px in the layout; the expanded list (248 px) floats over the stage
 * on mouse hover, or after a click or tap on a category while collapsed (the
 * keyboard and touch path), and closes on Escape, a press outside or focus
 * moving out of it. Pinned on a desk-width screen it takes its 248 px in the
 * layout instead; below 1100 px it always floats (`desk` false), pinned or not.
 */
export function CommandStrip({ mode, desk }: { mode: Mode; desk: boolean }) {
  const navigate = useNavigate();
  const route = useParams();
  const examplesView = useCatalogueView((s) => s.examples);
  const selectedNetwork = useLoader(s => s.network);
  const examples = mode === 'maintenance' && (examplesView || selectedNetwork === 'full_catalogue');
  const catalogue = useMaintenanceCatalogue(examples);
  const [search, setSearch] = useState({ network: '', text: '' });
  const filter = search.network === catalogue.network ? search.text : '';
  const setFilter = (text: string) => setSearch({ network: catalogue.network, text });
  const [limit, setLimit] = useState(80);
  const pinned = useUI((s) => s.stripPinned);
  const togglePinned = useUI((s) => s.toggleStripPinned);
  const gaiaOpen = useUI((s) => s.gaiaOpen);
  const setGaiaOpen = useUI((s) => s.setGaiaOpen);
  const selection = useUI((s) => s.selection);
  const select = useUI((s) => s.select);
  const [hover, setHover] = useState(false);
  const [opened, setOpened] = useState(false);
  const [open, setOpen] = useState<string>('antennas');
  const navRef = useRef<HTMLElement>(null);
  const expanded = pinned || hover || opened;
  const inFlowWide = desk && pinned;
  const floating = expanded && !inFlowWide;

  // A click-opened overlay closes on Escape (capture phase, so the shell does not also clear the
  // selection) and on a press outside the strip. Focus inside goes back to the open category.
  useEffect(() => {
    if (!opened || pinned) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      const nav = navRef.current;
      const focusInside = nav?.contains(document.activeElement) ?? false;
      setOpened(false);
      if (focusInside) {
        const section =
          nav?.querySelector<HTMLElement>('[data-strip-section][aria-expanded="true"]') ??
          nav?.querySelector<HTMLElement>('[data-strip-section]');
        section?.focus();
      }
    };
    const onDown = (e: globalThis.PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpened(false);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [opened, pinned]);

  const onPointerEnter = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') setHover(true);
  };
  const onPointerLeave = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') setHover(false);
  };
  /** A category press while collapsed opens the overlay on it; while expanded it toggles the category. */
  const pressSection = (key: string) => {
    if (!expanded) {
      setOpened(true);
      setOpen(key);
    } else setOpen((o) => (o === key ? '' : key));
  };
  /** Keyboard focus that leaves the strip takes a click-opened overlay with it. */
  const onBlur = (e: FocusEvent<HTMLElement>) => {
    if (opened && !pinned && !e.currentTarget.contains(e.relatedTarget as Node | null)) setOpened(false);
  };
  /** After a pick on a tablet the overlay gets out of the way of the stage. */
  const afterPick = () => {
    if (!desk && !pinned) setOpened(false);
  };

  const tree = useQuery({
    queryKey: ['assets', 'tree'],
    queryFn: () => getJson<TreeNode[]>('/data/actionable_items.json'),
    staleTime: Infinity,
  });

  const legacyLeaves = (g: Group): TreeNode[] =>
    g.key === 'noaa'
      ? NOAA_CATALOGUE.entries.map((e) => ({
          id: noaaAssetId(e.id),
          name: e.name,
          description: `${e.role}. ${e.status}. ${e.model}. Reviewed ${e.checkedAt}.`,
        }))
      : (tree.data ?? []).filter((n) => g.sources.includes(n.id)).flatMap((n) => n.children ?? []);

  const catalogueMode = mode === 'maintenance' && catalogue.network !== 'demo';
  const groups = catalogueMode
    ? catalogue.network === 'rfims'
      ? GROUPS.filter((g) => ['overview', 'antennas'].includes(g.key))
      : GROUPS.filter((g) => !['satellites'].includes(g.key)).map((g) =>
          g.key === 'noaa'
            ? { ...g, label: catalogue.network === 'noaa' ? 'NOAA satellites' : 'Satellites' }
            : g.key === 'sensor-sources'
              ? { ...g, label: 'Demonstration sources' }
              : g,
        )
    : GROUPS.filter((g) => !examples || g.key !== 'noaa');
  const leavesFor = (g: Group): CatalogueLeaf[] => {
    if (!catalogueMode) return legacyLeaves(g).filter((l) => !l.id.startsWith('rfims'));
    if (catalogue.network === 'rfims')
      return g.key === 'antennas' ? RFIMS_SITES.map(s => ({ id: s.id, name: `${s.rmsId} · ${s.name}`, description: s.siteId })) : [];
    if (catalogue.groups[g.key]) return catalogue.groups[g.key];
    if (g.key === 'sensor-sources' && !['noaa', 'nasa', 'noaa_extended'].includes(catalogue.network))
      return [];
    if (g.key === 'consumers' && catalogue.network !== 'noaa') return [];
    return legacyLeaves(g).map((l) => ({
      ...l,
      description: `${l.description ?? ''} Representative demonstration asset; not part of the operated spacecraft or antenna inventory.`,
    }));
  };
  const pick = async (leaf: CatalogueLeaf) => {
    try {
      const id = leaf.antenna
        ? registerAntennaTwin(leaf.antenna, catalogue.network)
        : leaf.mission
          ? await registerMissionTwin(leaf.mission, catalogue.network)
          : leaf.id;
      select({ assetId: id, subsystemId: null });
      // Leave a previous deep link behind so a catalogue change cannot reload it.
      if (mode === 'overview') navigate(`/twin/${encodeURIComponent(id)}`);
      else if (route.assetId) navigate('/maintenance');
      afterPick();
    } catch (e) {
      toast('error', `Could not open the twin: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  const previous = useRef({ network: catalogue.network, mode });
  useEffect(() => {
    const changed = previous.current.network !== catalogue.network || previous.current.mode !== mode;
    previous.current = { network: catalogue.network, mode };
    if (mode !== 'maintenance' || route.assetId) return;
    const selected = useUI.getState().selection.assetId;
    const allowed = Object.values(catalogue.groups).some((leaves) => leaves.some((l) => l.id === selected));
    const reference =
      ['noaa', 'nasa', 'noaa_extended'].includes(catalogue.network) &&
      [
        'er_2',
        'boat',
        'terrestrial',
        'soldier',
        'weather_center',
        'data_cloud',
        'rf_antenna',
        'optical_antenna',
        'ground_station',
        'data_center',
      ].includes(selected ?? '');
    if (
      selected &&
      (allowed ||
        reference ||
        selected.startsWith(`mission:${catalogue.network}:`) ||
        selected.startsWith(`${catalogue.network}:`) ||
        (catalogue.network === 'demo' && !selected.includes(':') && !selected.startsWith('rfims')) ||
        (catalogue.network === 'noaa' && selected === 'senseSat_01') ||
        (catalogue.network === 'rfims' && selected.startsWith('rfims')))
    )
      return;
    if (catalogue.network === 'rfims') select({ assetId: 'rfims00', subsystemId: null });
    else if (catalogue.network === 'noaa' || catalogue.network === 'demo')
      select({ assetId: 'senseSat_01', subsystemId: null });
    else {
      const first = catalogue.groups.noaa[0] ?? catalogue.groups.antennas[0];
      if (first)
        void (
          first.mission
            ? registerMissionTwin(first.mission, catalogue.network)
            : Promise.resolve(registerAntennaTwin(first.antenna!, catalogue.network))
        )
          .then((id) => {
            if (
              !useCatalogueView.getState().examples && useLoader.getState().network === catalogue.network &&
              useUI.getState().selection.assetId === selected
            )
              select({ assetId: id, subsystemId: null });
          })
          .catch(() => {});
      else if (changed) select({ assetId: null, subsystemId: null });
    }
  }, [catalogue.network, catalogue.groups, mode, route.assetId, selection.assetId, select]);

  return (
    <nav
      ref={navRef}
      aria-label="Assets"
      data-testid="command-strip"
      data-expanded={expanded ? '' : undefined}
      data-presentation={desk ? 'rail' : 'overlay'}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onBlur={onBlur}
      className={cn(
        'relative z-30 shrink-0 transition-[width] duration-200',
        inFlowWide ? 'w-[248px]' : 'w-16',
      )}
    >
      <div
        className={cn(
          'absolute inset-y-0 left-0 flex flex-col border-r border-hairline bg-raised transition-[width,box-shadow] duration-200',
          expanded ? 'w-[248px]' : 'w-16',
          floating && 'shadow-elev-2',
        )}
      >
        {GLOBE_MODES.has(mode) ? (
          <LoaderStrip expanded={expanded} onOpenRequest={() => setOpened(true)} onPicked={afterPick} />
        ) : (
          <div className="flex-1 overflow-y-auto overflow-x-hidden py-2 scrollbar-thin">
            {expanded && catalogueMode && (
              <div className="px-3 pb-2">
                <input
                  aria-label="Find catalogue asset"
                  placeholder="Find spacecraft, site or antenna"
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setLimit(80);
                  }}
                  className="w-full rounded-control border border-hairline bg-canvas px-2 py-1.5 text-xs text-ink"
                />
                {catalogue.loading && catalogue.network !== 'rfims' && (
                  <p role="status" className="mt-1 text-xs text-ink-3">
                    Loading catalogue…
                  </p>
                )}
                {catalogue.error && (
                  <button className="text-xs text-warning underline" onClick={catalogue.retry}>
                    Retry catalogue
                  </button>
                )}
              </div>
            )}
            {groups.map((g) => {
              const Icon = g.icon;
              const leaves = leavesFor(g);
              const matches = leaves.filter(
                (l) =>
                  !filter || `${l.name} ${l.description ?? ''}`.toLowerCase().includes(filter.toLowerCase()),
              );
              const isOpen = expanded && (open === g.key || Boolean(filter)) && matches.length > 0;
              const groupActive =
                g.key === 'overview' ? mode === 'overview' : leaves.some((l) => l.id === selection.assetId);
              return (
                <div key={g.key}>
                  <button
                    type="button"
                    title={g.label}
                    aria-label={expanded ? undefined : g.label}
                    aria-expanded={g.key === 'overview' ? undefined : isOpen}
                    aria-current={g.key === 'overview' && mode === 'overview' ? 'page' : undefined}
                    data-strip-section=""
                    onClick={() => {
                      if (g.key === 'overview') navigate('/overview');
                      else pressSection(g.key);
                    }}
                    className={cn(
                      'flex h-11 w-full items-center gap-3 px-[23px] text-left text-xs font-medium tracking-wide transition hover:bg-overlay focus-visible:outline-offset-[-2px]',
                      groupActive ? 'text-accent' : 'text-ink-2',
                    )}
                  >
                    <Icon aria-hidden className="h-[18px] w-[18px] shrink-0" />
                    {expanded && <span className="truncate">{g.label}</span>}
                    {expanded && leaves.length > 0 && (
                      <span className="ml-auto text-[10px] text-ink-3">{leaves.length}</span>
                    )}
                  </button>
                  {isOpen && (
                    <ul className="mb-1 ml-4 border-l border-hairline">
                      {matches.slice(0, limit).map((leaf) => {
                        const active = leaf.id === selection.assetId;
                        return (
                          <li key={leaf.id}>
                            <button
                              type="button"
                              title={leaf.description}
                              aria-current={active ? 'true' : undefined}
                              onClick={() => void pick(leaf)}
                              className={cn(
                                'flex w-full items-center gap-2 py-cell-y pl-4 pr-3 text-left text-data transition hover:bg-overlay focus-visible:outline-offset-[-2px]',
                                active ? 'font-semibold text-accent' : 'text-ink-2',
                              )}
                            >
                              <span
                                aria-hidden
                                className={cn(
                                  'h-1.5 w-1.5 shrink-0 rounded-full',
                                  active ? 'bg-accent' : 'bg-nominal',
                                )}
                              />
                              <span className="truncate">{leaf.name}</span>
                            </button>
                          </li>
                        );
                      })}
                      {matches.length > limit && (
                        <li>
                          <button
                            className="px-4 py-2 text-xs text-accent"
                            onClick={() => setLimit((n) => n + 80)}
                          >
                            Show more ({matches.length - limit})
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <button
          type="button"
          onClick={() => setGaiaOpen(!gaiaOpen)}
          title={gaiaOpen ? 'Close GAIA' : 'Ask GAIA'}
          aria-label={gaiaOpen ? 'Close GAIA' : 'Ask GAIA'}
          aria-pressed={gaiaOpen}
          className={cn(
            'flex h-10 shrink-0 items-center gap-3 border-t border-hairline px-6 text-xs hover:text-ink focus-visible:outline-offset-[-2px]',
            gaiaOpen ? 'text-accent' : 'text-ink-2',
          )}
        >
          <Bot aria-hidden className="h-4 w-4 shrink-0" />
          {expanded && <span>GAIA</span>}
        </button>
        <button
          type="button"
          onClick={togglePinned}
          title={pinned ? 'Unpin the strip' : 'Pin the strip open'}
          aria-label={pinned ? 'Unpin the strip' : 'Pin the strip open'}
          aria-pressed={pinned}
          className="flex h-10 shrink-0 items-center gap-3 border-t border-hairline px-6 text-xs text-ink-2 hover:text-ink focus-visible:outline-offset-[-2px]"
        >
          {pinned ? (
            <PinOff aria-hidden className="h-4 w-4 shrink-0" />
          ) : (
            <Pin aria-hidden className="h-4 w-4 shrink-0" />
          )}
          {expanded && <span>{pinned ? 'Unpin' : 'Pin open'}</span>}
        </button>
      </div>
    </nav>
  );
}
